/**
 * Katalog der erfassbaren Anlagen im Schritt „Sicherheit" — die EINE Quelle,
 * aus der Beschriftung, Gruppenzuordnung, Errichter-Regel und Zusatzfelder
 * kommen.
 *
 * Warum als Daten und nicht als Markup: der Auftraggeber hat am 19.08.2026 aus
 * fünf Anlagen 28 gemacht. Als kopierte Frageblöcke wären das 28 gleich
 * aussehende `if`-Zweige, von denen bei der nächsten Katalogänderung
 * unweigerlich einer stehen bleibt. `AnlagenChecklist` rendert deshalb je
 * Eintrag genau EINEN `AnlagenBlock` und liest jede Abweichung hier ab.
 *
 * **Die `key`s sind Antwort-Schlüssel.** Sie landen als `sicherheit_<key>` in
 * `Gebaeude.answers` und damit in den `RiskCharacteristics`. Die fünf
 * Hauptanlagen (`bma`, `ema`, `rwa`, `sprinkler`, `blitzschutz`) tragen
 * deshalb unverändert ihre alten Schlüssel und ihre alten Beschriftungen —
 * `sicherheit_bma` … `sicherheit_blitzschutz` bleiben byte-identisch.
 *
 * Reihenfolge, Wortlaut und Marker stammen wörtlich aus der Fach-Spezifikation
 * und sind verbindlich; sie sind hier bewusst nicht „geglättet" (siehe die
 * Ausführung der Steigleitungen mit ß).
 */

/** Die drei Abschnitte des Schritts. */
export type AnlagenGruppeKey = "haupt" | "feuer" | "elektro";

/**
 * Der Marker aus der Spezifikation.
 *
 * - `voll` (dort „*" oder ohne Marker): das Folgefragen-Set vollständig,
 *   inklusive „Errichter / Installationsfirma".
 * - `ohneErrichter` (dort „**"): dasselbe Set OHNE „Errichter /
 *   Installationsfirma" — bei einem Feuerlöscher oder einer Brandschutztür
 *   gibt es keine Errichterfirma, nach der sich fragen ließe.
 */
export type AnlagenMarker = "voll" | "ohneErrichter";

/**
 * Ein Zusatzfeld, das NUR diese eine Anlage kennt. Es steht am Anfang des
 * Detailblocks, vor dem für alle Anlagen gleichen Folgefragen-Set, und wird
 * unter seinem `key` als Unterschlüssel des Anlagen-Merkmals gespeichert.
 */
export type AnlagenExtraFeld = {
  key: string;
  label: string;
  type: "select" | "text";
  options?: readonly string[];
};

export type AnlagenKatalogEintrag = {
  /** Antwort-Schlüssel (`sicherheit_<key>`) und zugleich Motiv-Schlüssel. */
  key: string;
  label: string;
  gruppe: AnlagenGruppeKey;
  marker: AnlagenMarker;
  extraFields?: AnlagenExtraFeld[];
};

/**
 * Die Abschnittsüberschriften. „Feuer, Brand" und „Elektrische Anlagen und
 * Betriebsmittel allgemein" sind ABSCHNITTE innerhalb der einen Frage
 * „Welche der folgenden Anlagen sind vorhanden (Mehrfachauswahl möglich)?" —
 * keine eigenen Fragen. Die Hauptanlagen stehen davor und werden einzeln mit
 * Ja/Nein beantwortet.
 */
export const ANLAGEN_GRUPPEN: readonly { key: AnlagenGruppeKey; titel: string }[] = [
  { key: "haupt", titel: "Sicherheitsanlagen" },
  { key: "feuer", titel: "Feuer, Brand" },
  { key: "elektro", titel: "Elektrische Anlagen und Betriebsmittel allgemein" },
];

/** Wörtlicher Wortlaut der Auswahlfrage über den beiden Abschnitten. */
export const ANLAGEN_AUSWAHL_FRAGE =
  "Welche der folgenden Anlagen sind vorhanden (Mehrfachauswahl möglich)?";

export const ANLAGEN_KATALOG: readonly AnlagenKatalogEintrag[] = [
  // ── Hauptanlagen: Schlüssel und Beschriftungen unverändert seit der ersten
  //    Fassung des Schritts. Sie tragen `voll`, also mit Errichter, und neu
  //    das Feld „Wert (Neuwert, brutto)" aus dem gemeinsamen Folgefragen-Set.
  { key: "bma", label: "Brandmeldeanlage (BMA)", gruppe: "haupt", marker: "voll" },
  { key: "ema", label: "Einbruchmeldeanlage (EMA)", gruppe: "haupt", marker: "voll" },
  {
    key: "rwa",
    label: "Rauch- und Wärmeabzugsanlage (RWA)",
    gruppe: "haupt",
    marker: "voll",
  },
  { key: "sprinkler", label: "Sprinkleranlage", gruppe: "haupt", marker: "voll" },
  { key: "blitzschutz", label: "Blitzschutzanlage", gruppe: "haupt", marker: "voll" },

  // ── Feuer, Brand (16 Einträge, Reihenfolge der Spezifikation)
  {
    key: "rauchwarnmelder",
    label: "Rauchwarnmelder",
    gruppe: "feuer",
    marker: "ohneErrichter",
  },
  { key: "feuerloescher", label: "Feuerlöscher", gruppe: "feuer", marker: "ohneErrichter" },
  {
    key: "brandschutztueren",
    label: "Brandschutztüren / -abschlüsse",
    gruppe: "feuer",
    marker: "ohneErrichter",
  },
  { key: "wandhydranten", label: "Wandhydranten", gruppe: "feuer", marker: "ohneErrichter" },
  { key: "funkenloeschanlagen", label: "Funkenlöschanlagen", gruppe: "feuer", marker: "voll" },
  {
    key: "gasfeuerloeschanlagen",
    label: "Gas-Feuerlöschanlagen",
    gruppe: "feuer",
    marker: "voll",
  },
  { key: "stickstoffanlage", label: "Stickstoffanlage", gruppe: "feuer", marker: "voll" },
  { key: "kleinloeschanlagen", label: "Kleinlöschanlagen", gruppe: "feuer", marker: "voll" },
  {
    key: "schaumloeschanlagen",
    label: "Ortsfeste Schaumlöschanlagen",
    gruppe: "feuer",
    marker: "voll",
  },
  {
    key: "spruehwasserloeschanlagen",
    label: "Sprühwasserlöschanlagen",
    gruppe: "feuer",
    marker: "voll",
  },
  {
    key: "entrauchungsanlagen",
    label: "Maschinelle Entrauchungsanlagen",
    gruppe: "feuer",
    marker: "voll",
  },
  {
    key: "foerderanlagenabschluesse",
    label: "Förderanlagenabschlüsse",
    gruppe: "feuer",
    marker: "voll",
  },
  {
    key: "brandschutzklappen",
    label: "Brandschutzklappen",
    gruppe: "feuer",
    marker: "ohneErrichter",
  },
  {
    key: "brandschottungen",
    label: "Brandschottungen",
    gruppe: "feuer",
    marker: "ohneErrichter",
  },
  {
    key: "pulverloeschanlagen",
    label: "Ortsfeste Pulverlöschanlagen",
    gruppe: "feuer",
    marker: "voll",
  },
  {
    key: "steigleitungen",
    label: "Steigleitungen",
    gruppe: "feuer",
    marker: "ohneErrichter",
    /**
     * Die drei Werte stehen so in der Spezifikation — MIT ß. „naß" ist hier
     * keine alte Rechtschreibung, die sich nebenbei modernisieren ließe,
     * sondern der Fachbegriff der Feuerwehr-Steigleitung (Nass-, Nass-Trocken-
     * und Trockensteigleitung) in der Schreibweise des Auftraggebers. Wer ihn
     * zu „nass" glättet, ändert einen gespeicherten Antwortwert.
     */
    extraFields: [
      {
        key: "bauart",
        label: "Ausführung",
        type: "select",
        options: ["naß", "naß-trocken", "trocken"],
      },
    ],
  },

  // ── Elektrische Anlagen und Betriebsmittel allgemein (7 Einträge)
  {
    key: "notbeleuchtung",
    label: "Notbeleuchtung / Notausgänge",
    gruppe: "elektro",
    marker: "ohneErrichter",
  },
  {
    key: "batterieladestation",
    label: "Batterieladestation",
    gruppe: "elektro",
    marker: "ohneErrichter",
  },
  {
    key: "kompressorenLueftung",
    label: "Kompressoren f. Lüftung u. Druckluft",
    gruppe: "elektro",
    marker: "ohneErrichter",
  },
  { key: "netzersatzanlage", label: "Netzersatzanlage", gruppe: "elektro", marker: "voll" },
  {
    key: "netzersatzanlageDiesel",
    label: "Notstromversorgung Netzersatzanlage (Diesel)",
    gruppe: "elektro",
    marker: "voll",
  },
  {
    key: "fiSchutzeinrichtungen",
    label: "Fehlerstrom- oder Fehlerspannungsschutzeinrichtungen",
    gruppe: "elektro",
    marker: "voll",
  },
  { key: "feststellanlagen", label: "Feststellanlagen", gruppe: "elektro", marker: "voll" },
];

/** Die Einträge einer Gruppe, in Katalogreihenfolge. */
export const anlagenDerGruppe = (
  gruppe: AnlagenGruppeKey,
): readonly AnlagenKatalogEintrag[] =>
  ANLAGEN_KATALOG.filter((eintrag) => eintrag.gruppe === gruppe);

/**
 * Die Marker-Regel an EINER Stelle: „**" (`ohneErrichter`) blendet das Feld
 * „Errichter / Installationsfirma" aus, alles andere zeigt es.
 */
export const zeigtErrichter = (eintrag: AnlagenKatalogEintrag): boolean =>
  eintrag.marker !== "ohneErrichter";

/** Antwort-Schlüssel eines Katalogeintrags im Antwort-Bag des Gebäudes. */
export const anlagenAnswerKey = (key: string): string => `sicherheit_${key}`;

/** Slot-Schlüssel der Nachweise einer Anlage (ohne Gebäude-Scope). */
export const anlagenSlotKey = (key: string): string => `sicherheit:${key}`;
